import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { TextField, Button } from "@mui/material";
import { api } from "../../../utils/api";
import { fetchVehicleDetails } from "../../../utils/fetchVehicleDetails";
import { showSuccessToast } from "../../../utils/toast";
import { debounce } from "lodash";
import { COLORS } from "../../../utils/colors";
import {
  getVehicleStateFieldLabel,
  getVehicleStateOptionsForSelect
} from "../../../utils/p1Region";

const validationSchema = Yup.object().shape({
  rego: Yup.string().required("Vehicle registration is required"),
  vehicleState: Yup.string().required("Vehicle state or region is required"),
  vin: Yup.string(),
  make: Yup.string(),
  model: Yup.string(),
  modelYear: Yup.string().matches(/^\d{4}$|^$/, "Model year must be YYYY format"),
  color: Yup.string()
});

const AddVehicleToCustomerForm = ({ customer, subscriptionPreview, callback, closeModal }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("enter");
  const [searchRego, setSearchRego] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const handleVehicleStateChange = debounce(async (setFieldValue, rego, state) => {
    if (rego && state) {
      try {
        const vehicleDetails = await fetchVehicleDetails(rego, state);
        if (vehicleDetails) {
          setFieldValue("vin", vehicleDetails.vin || "");
          setFieldValue("make", vehicleDetails.make || "");
          setFieldValue("model", vehicleDetails.model || "");
          if (vehicleDetails.compliance_plate) {
            const year = vehicleDetails.compliance_plate.split("-")[0];
            if (year) setFieldValue("modelYear", year);
          }
        }
      } catch (err) {
        console.error("Error fetching vehicle details:", err);
      }
    }
  }, 500);

  const handleSearchByRego = async () => {
    const regoNorm = (searchRego || "").trim().toUpperCase();
    if (!regoNorm) {
      setError("Enter a registration number to search.");
      return;
    }
    setError("");
    setSearchLoading(true);
    setSearchResults([]);
    setSelectedVehicle(null);
    try {
      const response = await api(
        `v1/customer/${customer._id}/vehicle-search?rego=${encodeURIComponent(regoNorm)}`,
        "GET"
      );
      if (response.data.success && response.data.data?.vehicles) {
        setSearchResults(response.data.data.vehicles);
        if (response.data.data.vehicles.length === 0) {
          setError("No vehicles found for this registration.");
        }
      } else {
        setError(response.data.message || "Search failed.");
      }
    } catch (err) {
      console.error("Error searching vehicles:", err);
      setError(err.response?.data?.message || "Search failed. Please try again.");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSubmitEnter = async (values, { setSubmitting }) => {
    setLoading(true);
    setError("");

    try {
      const response = await api(`v1/customer/${customer._id}/add-vehicle`, "POST", {
        rego: values.rego.trim().toUpperCase(),
        vin: values.vin?.trim() || "",
        make: values.make?.trim() || "",
        model: values.model?.trim() || "",
        modelYear: values.modelYear?.trim() || "",
        color: values.color?.trim() || "",
        vehicleType: values.vehicleType || null
      });

      if (response.data.success) {
        showSuccessToast(response.data.message || "Vehicle added successfully.");
        callback();
        closeModal();
      } else {
        setError(response.data.message || "Failed to add vehicle");
      }
    } catch (err) {
      console.error("Error adding vehicle:", err);
      setError(err.response?.data?.message || "Failed to add vehicle. Please try again.");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleSubmitSearch = async () => {
    if (!selectedVehicle) return;
    setLoading(true);
    setError("");

    try {
      const response = await api(`v1/customer/${customer._id}/add-vehicle`, "POST", {
        vehicleId: selectedVehicle._id
      });

      if (response.data.success) {
        showSuccessToast(response.data.message || "Vehicle linked successfully.");
        callback();
        closeModal();
      } else {
        setError(response.data.message || "Failed to link vehicle");
      }
    } catch (err) {
      console.error("Error linking vehicle:", err);
      setError(err.response?.data?.message || "Failed to link vehicle. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-column gap-4 w-full">
      {subscriptionPreview && (
        <div className="p-3 mb-2 rounded-lg bg-blue-50 border border-blue-200">
          <p className="text-xs font-semibold text-blue-800 mb-1">Subscription that will be used to link the vehicle</p>
          <p className="text-sm font-medium text-blue-900">
            {subscriptionPreview.productName || subscriptionPreview.description}
          </p>
          {subscriptionPreview.amountFormatted && (
            <p className="text-sm text-blue-900 mt-0.5">{subscriptionPreview.amountFormatted}</p>
          )}
          {subscriptionPreview.stripeSubscriptionId && (
            <p className="text-xs text-blue-700 mt-1 font-mono truncate" title={subscriptionPreview.stripeSubscriptionId}>
              Stripe: {subscriptionPreview.stripeSubscriptionId}
            </p>
          )}
          <p className="text-xs text-blue-700 mt-1">
            This subscription will be used to link the vehicle details you enter below, or the vehicle you select from search by registration.
          </p>
        </div>
      )}
      <div className="text-sm text-gray-600 mb-2">
        Add or update vehicle details. This is available when the customer has: a subscription linked to a vehicle
        with DEFAULTREGO (placeholder), an orphaned subscription, or a valid Stripe subscription not yet linked to a
        vehicle.
      </div>

      <div className="flex gap-2 mb-2">
        <Button
          variant={mode === "enter" ? "contained" : "outlined"}
          size="small"
          onClick={() => {
            setMode("enter");
            setError("");
            setSearchResults([]);
            setSelectedVehicle(null);
          }}
          sx={mode === "enter" ? { backgroundColor: COLORS.priorityBlue?.hex || "#10073E" } : {}}
        >
          Enter vehicle details
        </Button>
        <Button
          variant={mode === "search" ? "contained" : "outlined"}
          size="small"
          onClick={() => {
            setMode("search");
            setError("");
          }}
          sx={mode === "search" ? { backgroundColor: COLORS.priorityBlue?.hex || "#10073E" } : {}}
        >
          Search by registration
        </Button>
      </div>

      {mode === "search" ? (
        <div className="flex flex-column gap-4">
          <div className="flex gap-2 align-items-center flex-wrap">
            <TextField
              label="Registration"
              variant="outlined"
              size="small"
              value={searchRego}
              onChange={(e) => setSearchRego(e.target.value.toUpperCase())}
              inputProps={{ style: { textTransform: "uppercase" }, maxLength: 10 }}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleSearchByRego())}
            />
            <Button
              variant="contained"
              onClick={handleSearchByRego}
              disabled={searchLoading}
              sx={{ backgroundColor: COLORS.priorityBlue?.hex || "#10073E" }}
            >
              {searchLoading ? "Searching..." : "Search"}
            </Button>
          </div>
          {searchResults.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Select a vehicle to link to this customer:</p>
              <div className="flex flex-column gap-2 max-h-60 overflow-y-auto">
                {searchResults.map((v) => (
                  <div
                    key={v._id}
                    onClick={() => setSelectedVehicle(selectedVehicle?._id === v._id ? null : v)}
                    className={`p-3 border rounded-lg cursor-pointer ${
                      selectedVehicle?._id === v._id
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-400"
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-medium">{v.rego}</span>
                        {v.make && <span className="ml-2">{v.make}</span>}
                        {v.model && <span className="ml-1">{typeof v.model === "string" ? v.model : ""}</span>}
                        {v.modelYear && <span className="ml-1">({v.modelYear})</span>}
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-200">
                        {v.hasSubscription ? "Has subscription" : "Unassigned"}
                      </span>
                    </div>
                    {v.vin && <p className="text-xs text-gray-500 mt-1">VIN: {v.vin}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {error && (
            <div className="text-red-500 text-sm p-2 bg-red-50 rounded">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-4 mt-4">
            <Button variant="outlined" onClick={closeModal} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitSearch}
              disabled={loading || !selectedVehicle}
              sx={{ backgroundColor: COLORS.priorityBlue?.hex || "#10073E" }}
            >
              {loading ? "Linking..." : "Add Vehicle"}
            </Button>
          </div>
        </div>
      ) : (
        <Formik
          initialValues={{
            rego: "",
            vehicleState: "",
            vin: "",
            make: "",
            model: "",
            modelYear: "",
            color: "",
            vehicleType: ""
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmitEnter}
        >
          {({ values, setFieldValue, errors, touched }) => (
            <Form className="flex flex-column gap-4 w-full">
              <TextField
                label="Vehicle Registration"
                name="rego"
                variant="outlined"
                fullWidth
                value={values.rego}
                onChange={(e) => {
                  setFieldValue("rego", e.target.value.toUpperCase());
                  if (values.vehicleState) {
                    handleVehicleStateChange(setFieldValue, e.target.value, values.vehicleState);
                  }
                }}
                error={touched.rego && !!errors.rego}
                helperText={touched.rego && errors.rego}
                required
                inputProps={{ style: { textTransform: "uppercase" } }}
              />

              <div>
                <label htmlFor="vehicleState" className="block mb-2 text-sm font-medium">
                  {getVehicleStateFieldLabel()} <span className="text-red-500">*</span>
                </label>
                <Field
                  as="select"
                  id="vehicleState"
                  name="vehicleState"
                  onChange={(e) => {
                    const state = e.target.value;
                    setFieldValue("vehicleState", state);
                    if (values.rego) {
                      handleVehicleStateChange(setFieldValue, values.rego, state);
                    }
                  }}
                  className="block w-full px-4 py-3 text-base text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">---SELECT {getVehicleStateFieldLabel().toUpperCase()}---</option>
                  {getVehicleStateOptionsForSelect(values.vehicleState).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Field>
                <ErrorMessage name="vehicleState" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <TextField
                label="VIN"
                name="vin"
                variant="outlined"
                fullWidth
                value={values.vin}
                onChange={(e) => setFieldValue("vin", e.target.value.toUpperCase())}
                inputProps={{ maxLength: 17 }}
                error={touched.vin && !!errors.vin}
                helperText={touched.vin && errors.vin}
              />

              <TextField
                label="Make"
                name="make"
                variant="outlined"
                fullWidth
                value={values.make}
                onChange={(e) => setFieldValue("make", e.target.value)}
                error={touched.make && !!errors.make}
                helperText={touched.make && errors.make}
              />

              <TextField
                label="Model"
                name="model"
                variant="outlined"
                fullWidth
                value={values.model}
                onChange={(e) => setFieldValue("model", e.target.value)}
                error={touched.model && !!errors.model}
                helperText={touched.model && errors.model}
              />

              <TextField
                label="Model Year"
                name="modelYear"
                variant="outlined"
                fullWidth
                value={values.modelYear}
                onChange={(e) => setFieldValue("modelYear", e.target.value)}
                inputProps={{ maxLength: 4 }}
                error={touched.modelYear && !!errors.modelYear}
                helperText={touched.modelYear && errors.modelYear}
              />

              <TextField
                label="Color"
                name="color"
                variant="outlined"
                fullWidth
                value={values.color}
                onChange={(e) => setFieldValue("color", e.target.value)}
                error={touched.color && !!errors.color}
                helperText={touched.color && errors.color}
              />

              {error && (
                <div className="text-red-500 text-sm p-2 bg-red-50 rounded">
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-4 mt-4">
                <Button variant="outlined" onClick={closeModal} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ backgroundColor: COLORS.priorityBlue?.hex || "#10073E" }}
                >
                  {loading ? "Adding..." : "Add Vehicle"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default AddVehicleToCustomerForm;
